// Shared buyer-returns primitives — single source of truth for the status /
// tab / reason / refund / shipping vocabularies the buyer surface renders.
// (mvp-returns Phase 2 — screens-buyer-app §1, §2; reused by Phases 3-5.)
//
// Design-system rule: no emoji, no hardcoded hex. Status colours map to Mantine
// palette tokens; the supplier ReturnStatusBadge atom is reused for rendering.
import {
  ArrowUUpLeftIcon,
  BankIcon,
  CalendarXIcon,
  CoinsIcon,
  CreditCardIcon,
  FileTextIcon,
  type Icon,
  MagnifyingGlassIcon,
  MoneyIcon,
  PackageIcon,
  QuestionIcon,
  SealCheckIcon,
  ShieldCheckIcon,
  TagIcon,
  TruckIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import type { MantineColor } from "@mantine/core";
import { i18n } from "@dehub/theme";
import type {
  IRefundTimelinePreview,
  IReturnOrderDetail,
  RefundMethod,
  ReturnReason,
  ReturnRequestStatus,
  ShippingResponsibility,
} from "../../../apis/return";

// ── Status → Mongolian label + Mantine colour + Phosphor icon ────────────────
// Colours follow screens-buyer-app §24 semantics mapped to palette tokens:
//   REQUESTED=yellow, PROPOSED=orange, DISPUTED=red, active(blue/indigo/brand),
//   completed(green/teal), cancelled/rejected(gray), transit-exception(red).
export interface StatusMeta {
  labelMn: string;
  color: MantineColor;
  Icon: Icon;
}

// color + icon only; the label resolves at call time via i18n (buyerApp:status.*).
const RETURN_STATUS_STYLE: Record<ReturnRequestStatus, { color: MantineColor; Icon: Icon }> = {
  REQUESTED                 : { color: "yellow", Icon: ArrowUUpLeftIcon },
  PROPOSED                  : { color: "orange", Icon: WarningIcon },
  DISPUTED                  : { color: "red",    Icon: WarningIcon },
  ACCEPTED                  : { color: "indigo", Icon: SealCheckIcon },
  AWAITING_PICKUP_SCHEDULE  : { color: "indigo", Icon: TruckIcon },
  AWAITING_SHIPMENT_INFO    : { color: "indigo", Icon: TruckIcon },
  IN_TRANSIT                : { color: "blue",   Icon: TruckIcon },
  IN_TRANSIT_EXCEPTION      : { color: "red",    Icon: WarningIcon },
  AWAITING_WAREHOUSE_RECEIPT: { color: "blue",   Icon: PackageIcon },
  AWAITING_RESOLUTION       : { color: "indigo", Icon: ArrowUUpLeftIcon },
  REFUND_PENDING            : { color: "blue",   Icon: CoinsIcon },
  COMPLETED                 : { color: "green",  Icon: SealCheckIcon },
  PARTIALLY_COMPLETED       : { color: "teal",   Icon: SealCheckIcon },
  REJECTED                  : { color: "gray",   Icon: CalendarXIcon },
  CANCELLED                 : { color: "gray",   Icon: CalendarXIcon },
};

export function statusLabelMn(status: ReturnRequestStatus): string {
  return i18n.t(`buyerApp:status.${status}`, status);
}

export function statusMeta(status: ReturnRequestStatus): StatusMeta {
  const style = RETURN_STATUS_STYLE[status] ?? { color: "gray" as MantineColor, Icon: ArrowUUpLeftIcon };
  return { labelMn: statusLabelMn(status), color: style.color, Icon: style.Icon };
}

// Buyer-spec status → colour map, derived from RETURN_STATUS_META (single source
// of truth). Passed into ReturnStatusBadge.colorMap so the buyer surface renders
// the buyer palette while supplier screens (which omit the prop) keep their own.
export const BUYER_STATUS_COLOR: Record<ReturnRequestStatus, MantineColor> = Object.fromEntries(
  Object.entries(RETURN_STATUS_STYLE).map(([status, style]) => [status, style.color]),
) as Record<ReturnRequestStatus, MantineColor>;

// ── Tab → statuses[] (screens-buyer-app §2 tab status mapping) ───────────────
// "Хүлээгдэж буй" <= REQUESTED, PROPOSED, DISPUTED
// "Идэвхтэй"      <= ACCEPTED, AWAITING_*, IN_TRANSIT*, REFUND_PENDING, AWAITING_RESOLUTION
// "Дууссан"       <= COMPLETED, PARTIALLY_COMPLETED
// "Цуцалсан"      <= CANCELLED, REJECTED
export type BuyerReturnsTabKey = "pending" | "active" | "completed" | "cancelled";

export const BUYER_RETURNS_TAB_STATUSES: Record<BuyerReturnsTabKey, ReturnRequestStatus[]> = {
  pending  : ["REQUESTED", "PROPOSED", "DISPUTED"],
  active   : [
    "ACCEPTED",
    "AWAITING_PICKUP_SCHEDULE",
    "AWAITING_SHIPMENT_INFO",
    "IN_TRANSIT",
    "IN_TRANSIT_EXCEPTION",
    "AWAITING_WAREHOUSE_RECEIPT",
    "AWAITING_RESOLUTION",
    "REFUND_PENDING",
  ],
  completed: ["COMPLETED", "PARTIALLY_COMPLETED"],
  cancelled: ["CANCELLED", "REJECTED"],
};

export interface BuyerReturnsTab {
  key: BuyerReturnsTabKey;
  /** i18n key suffix under buyerApp:tabs.* — resolved at render time. */
  labelKey: BuyerReturnsTabKey;
}

export const BUYER_RETURNS_TABS: BuyerReturnsTab[] = [
  { key: "pending",   labelKey: "pending" },
  { key: "active",    labelKey: "active" },
  { key: "completed", labelKey: "completed" },
  { key: "cancelled", labelKey: "cancelled" },
];

// Sums a tab's live count from the list response counts.byStatus (no flicker:
// returns undefined while counts are still loading).
export function buyerTabCount(
  key: BuyerReturnsTabKey,
  byStatus?: Partial<Record<ReturnRequestStatus, number>>,
): number | undefined {
  if (!byStatus) return undefined;
  return BUYER_RETURNS_TAB_STATUSES[key].reduce((sum, s) => sum + (byStatus[s] ?? 0), 0);
}

// "action required" tab indicator — buyer must respond to PROPOSED rows.
export const BUYER_ACTION_REQUIRED_STATUSES: ReturnRequestStatus[] = ["PROPOSED"];

// ── Reason → label + icon (screens-buyer-app §5) ─────────────────────────────
// Labels mirror the supplier REASON_LABEL (reason-badge atom) so wording stays
// consistent; the icon set is buyer-screen specific.
export interface ReasonMeta {
  Icon: Icon;
}

export const RETURN_REASON_META: Record<ReturnReason, ReasonMeta> = {
  NOT_RECEIVED        : { Icon: PackageIcon },
  DELIVERY_DAMAGED    : { Icon: WarningIcon },
  WRONG_ITEM_DELIVERED: { Icon: WarningIcon },
  CANCEL_BEFORE_SHIP  : { Icon: CalendarXIcon },
  DEFECTIVE           : { Icon: WarningIcon },
  QUALITY_ISSUE       : { Icon: TagIcon },
  NOT_AS_DESCRIBED    : { Icon: MagnifyingGlassIcon },
  CHANGED_MIND        : { Icon: ArrowUUpLeftIcon },
  EXPIRED             : { Icon: CalendarXIcon },
  CONSIGNMENT_UNSOLD  : { Icon: PackageIcon },
  WARRANTY_CLAIM      : { Icon: ShieldCheckIcon },
  RECALL              : { Icon: WarningIcon },
  PRICE_DISPUTE       : { Icon: MoneyIcon },
  OTHER               : { Icon: QuestionIcon },
};

export function reasonLabelMn(reason: ReturnReason): string {
  return i18n.t(`buyerApp:reason.${reason}`, reason);
}

// Step-2 reason groups (screens-buyer-app §5): RETURN reasons (delivered) vs
// SIMPLE reasons (not-yet-delivered).
export const RETURN_REASONS: ReturnReason[] = [
  "DEFECTIVE",
  "QUALITY_ISSUE",
  "NOT_AS_DESCRIBED",
  "CHANGED_MIND",
  "EXPIRED",
  "WARRANTY_CLAIM",
  "PRICE_DISPUTE",
  "OTHER",
];

export const SIMPLE_REASONS: ReturnReason[] = [
  "NOT_RECEIVED",
  "DELIVERY_DAMAGED",
  "WRONG_ITEM_DELIVERED",
  "CANCEL_BEFORE_SHIP",
  "RECALL",
  "OTHER",
];

// ── Refund method → label + ETA hint + icon (screens-buyer-app §6) ───────────
export interface RefundMethodMeta {
  Icon: Icon;
}

export const REFUND_METHOD_META: Record<RefundMethod, RefundMethodMeta> = {
  BANK_TRANSFER    : { Icon: BankIcon },
  CREDIT_BALANCE   : { Icon: CreditCardIcon },
  INVOICE_DEDUCTION: { Icon: FileTextIcon },
  CASH             : { Icon: MoneyIcon },
};

export function refundMethodLabelMn(method: RefundMethod): string {
  return i18n.t(`buyerApp:refundMethod.${method}`, method);
}

// B67: ETA text comes from POST /ord/returns/preview-refund-timeline (tenant SLA),
// never from static copy. No preview yet -> "-".
export function refundEtaLabel(preview?: IRefundTimelinePreview | null): string {
  if (!preview) return "-";
  const max = preview.estimatedRefundDays;
  const min = preview.estimatedRefundDaysMin;
  if (min != null && min !== max) return i18n.t("buyerApp:refundEtaRange", { min, max });
  return i18n.t("buyerApp:refundEtaDays", { count: max });
}

// B64: ONE eligibility source for the wizard order picker and the order page.
// Mirrors POST /ord/returns (logics/return/create.ts): the window runs from the
// delivery date whatever the order status. The server's isReturnEligible also
// requires orderStatus DELIVERED/CLOSED, which orders delivered through delivery
// notes often never reach (they stay AUTHORIZED), so it alone would block every
// return the server accepts.
export const isOrderReturnable = (
  order?: Pick<IReturnOrderDetail, "isReturnEligible" | "deliveredAt" | "returnWindowDaysRemaining"> | null,
): boolean =>
  !!order?.isReturnEligible || (!!order?.deliveredAt && (order.returnWindowDaysRemaining ?? 0) > 0);

// ── Shipping responsibility → label (screens-buyer-app §6, D11) ──────────────
export function shippingResponsibilityLabelMn(value: ShippingResponsibility): string {
  return i18n.t(`buyerApp:shipping.${value}`, value);
}

// ── Buyer route helpers (03-menu-structure §8) ───────────────────────────────
export const BUYER_RETURNS_ROUTES = {
  hub      : "/buyer-app/my-returns",
  newReturn: "/buyer-app/my-returns/new",
  detail   : (returnId: string) => `/buyer-app/my-returns/${returnId}`,
  // Path A — order-detail return wizard (orderId prefilled, Step-0 skipped).
  orderReturn: (orderId: string) => `/buyer-app/orders/${orderId}/return`,
  notifications: "/buyer-app/notifications",
} as const;

// ── Lining figures so amounts / dates / refs align vertically ────────────────
// One shared style object (DRY) — used by every numeric Text across the buyer
// surface (was duplicated inline as { fontVariantNumeric: "tabular-nums" }).
export const TNUM = { fontVariantNumeric: "tabular-nums" } as const;
